import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
/** A scrollbar for the image rail, with a larger invisible touch target. */
export function TimelineScrollbar({ viewportRef, count, active, valueText, getRange, onInteractionStart, onInteractionEnd, onNavigate, }) {
    const trackRef = React.useRef(null);
    const thumbRef = React.useRef(null);
    const dragRef = React.useRef(null);
    const rangeRef = React.useRef(getRange);
    const disabled = count < 2;
    // Use frame offsets rather than transformed image bounds: the existing
    // inertia layers must not change the thumb's size or its endpoints.
    const geometry = () => {
        const viewport = viewportRef.current;
        const track = trackRef.current;
        if (!viewport || !track)
            return null;
        const { start, end } = rangeRef.current();
        const range = Math.max(0, end - start);
        const width = track.clientWidth;
        const thumbWidth = Math.min(width, Math.max(24, viewport.clientWidth + range
            ? (width * viewport.clientWidth) / (viewport.clientWidth + range)
            : width));
        const travel = width - thumbWidth;
        const progress = range
            ? Math.max(0, Math.min(1, (viewport.scrollLeft - start) / range))
            : 0;
        return {
            viewport,
            track,
            start,
            range,
            thumbWidth,
            travel,
            left: progress * travel,
        };
    };
    const syncThumb = () => {
        const metrics = geometry();
        const thumb = thumbRef.current;
        if (!metrics || !thumb)
            return;
        thumb.style.width = `${metrics.thumbWidth}px`;
        thumb.style.transform = `translateX(${metrics.left}px)`;
    };
    React.useLayoutEffect(() => {
        rangeRef.current = getRange;
        syncThumb();
    });
    React.useEffect(() => {
        const viewport = viewportRef.current;
        const track = trackRef.current;
        if (!viewport || !track)
            return;
        const resize = new ResizeObserver(syncThumb);
        resize.observe(viewport);
        resize.observe(track);
        viewport.addEventListener("scroll", syncThumb, { passive: true });
        return () => {
            resize.disconnect();
            viewport.removeEventListener("scroll", syncThumb);
            dragRef.current = null;
            track.removeAttribute("data-dragging");
        };
    }, [viewportRef, count]);
    const movePointer = (clientX) => {
        const metrics = geometry();
        const drag = dragRef.current;
        if (!metrics || !drag || !metrics.travel)
            return;
        const left = clientX - metrics.track.getBoundingClientRect().left - drag.offset;
        metrics.viewport.scrollLeft =
            metrics.start +
                Math.max(0, Math.min(1, left / metrics.travel)) * metrics.range;
        syncThumb();
    };
    const endDrag = (event) => {
        if (dragRef.current?.pointerId !== event.pointerId)
            return;
        dragRef.current = null;
        event.currentTarget.removeAttribute("data-dragging");
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        onInteractionEnd();
    };
    return (_jsx("div", { ref: trackRef, role: "slider", "aria-label": "Company milestone position", "aria-orientation": "horizontal", "aria-valuemin": 1, "aria-valuemax": Math.max(1, count), "aria-valuenow": active + 1, "aria-valuetext": valueText, "aria-disabled": disabled, tabIndex: disabled ? -1 : 0, className: "focus-ring relative flex h-11 min-w-0 flex-1 touch-pan-y select-none items-center rounded-sm data-[dragging]:cursor-grabbing aria-disabled:cursor-default cursor-pointer", "data-timeline-scrollbar": true, onKeyDown: (event) => {
            if (disabled)
                return;
            let destination;
            switch (event.key) {
                case "ArrowLeft":
                case "ArrowDown":
                    destination = active - 1;
                    break;
                case "ArrowRight":
                case "ArrowUp":
                    destination = active + 1;
                    break;
                case "Home":
                    destination = 0;
                    break;
                case "End":
                    destination = count - 1;
                    break;
                default:
                    return;
            }
            event.preventDefault();
            onNavigate(destination);
        }, onPointerDown: (event) => {
            if (disabled ||
                !event.isPrimary ||
                event.button !== 0 ||
                dragRef.current)
                return;
            const metrics = geometry();
            if (!metrics)
                return;
            onInteractionStart();
            event.currentTarget.focus({ preventScroll: true });
            const x = event.clientX - metrics.track.getBoundingClientRect().left;
            dragRef.current = {
                pointerId: event.pointerId,
                offset: x >= metrics.left && x <= metrics.left + metrics.thumbWidth
                    ? x - metrics.left
                    : metrics.thumbWidth / 2,
            };
            event.currentTarget.setAttribute("data-dragging", "");
            event.currentTarget.setPointerCapture(event.pointerId);
            movePointer(event.clientX);
        }, onPointerMove: (event) => {
            if (dragRef.current?.pointerId === event.pointerId)
                movePointer(event.clientX);
        }, onPointerUp: endDrag, onPointerCancel: endDrag, onLostPointerCapture: endDrag, children: _jsx("span", { className: "pointer-events-none relative block h-1.5 w-full rounded-full bg-[#CAE8FF]", "data-timeline-scrollbar-track": true, children: _jsx("span", { ref: thumbRef, className: "absolute inset-y-0 left-0 rounded-full bg-[#0066AC]", "data-timeline-scrollbar-thumb": true }) }) }));
}
//# sourceMappingURL=timeline-scrollbar.js.map